/**
 * Variation rules, on the device.
 *
 * A port of `app/services/variations.py`, held to the same cases in
 * `tests/fixtures/costing_cases.json`. The phone has to be able to tell a tech
 * "this one needs the office" while they are standing in a comms room with no
 * signal, and it has to give the answer the server will give — because the
 * tech is about to say it out loud to a customer.
 *
 * The device raises and collects the signature. It does not decide the
 * outcome: `status` is not writable through sync, and the server applies this
 * same rule when the change arrives (D18). Running it here is so the tech
 * knows what is going to happen, not so the device can make it happen.
 */

export const DRAFT = "draft";
export const SUBMITTED = "submitted";
export const APPROVED = "approved";
export const DECLINED = "declined";
export const WITHDRAWN = "withdrawn";

export type VariationStatus = "draft" | "submitted" | "approved" | "declined" | "withdrawn";

export const OPEN: readonly VariationStatus[] = [DRAFT, SUBMITTED] as const;
export const SETTLED: readonly VariationStatus[] = [APPROVED, DECLINED, WITHDRAWN] as const;
/** What actually blocks a job. A draft is the tech still typing. */
export const PENDING: readonly VariationStatus[] = [SUBMITTED] as const;

export type Block = "none" | "variation_pending" | "over_quoted_hours";

export type Outcome = {
  status: VariationStatus;
  approval_source: "office" | "customer_signature" | null;
  /** Signed, but over the limit — so the office still has to see it. */
  needs_office: boolean;
  reason: string;
};

const AUD = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  minimumFractionDigits: 2,
});

/** Decide what a variation becomes when it is sent. Mirrors `variations.submit`. */
export function submit(input: {
  amount_cents: number;
  field_approval_limit_cents: number;
  has_signature: boolean;
  approver_is_office?: boolean;
}): Outcome {
  const { amount_cents, field_approval_limit_cents, has_signature, approver_is_office = false } = input;

  if (approver_is_office) {
    return { status: APPROVED, approval_source: "office", needs_office: false, reason: "Approved in the office." };
  }

  if (!has_signature) {
    return { status: SUBMITTED, approval_source: null, needs_office: false, reason: "Waiting on approval." };
  }

  if (amount_cents > field_approval_limit_cents) {
    return {
      status: SUBMITTED,
      approval_source: null,
      needs_office: true,
      reason:
        `Signed on site, but ${AUD.format(amount_cents / 100)} is over the ` +
        `${AUD.format(field_approval_limit_cents / 100)} field limit — the office still has to approve it.`,
    };
  }

  return {
    status: APPROVED,
    approval_source: "customer_signature",
    needs_office: false,
    reason: "Signed by the customer on site.",
  };
}

/** Whether a status change is allowed at all. Settled variations do not move. */
export function canTransition(current: string, target: string): boolean {
  if ((SETTLED as readonly string[]).includes(current)) return false;
  if (target === DRAFT) return false;
  return [SUBMITTED, APPROVED, DECLINED, WITHDRAWN].includes(target);
}

export type VariationSummary = {
  status: string;
  amount_cents?: number;
  hours?: number;
};

export type Standing = {
  block: Block;
  approved_cents: number;
  approved_hours: number;
  pending_count: number;
  hours_ceiling: number | null;
  warnings: string[];
};

export type StandingOptions = {
  quoted_hours?: number | null;
  hours_actual?: number;
  overrun_accepted_hours?: number | null;
  /** The entry about to be booked, counted before the decision. */
  proposed_hours?: number;
};

/**
 * Where a job stands, and whether more work is allowed on it.
 *
 * `proposed_hours` is counted before the decision rather than after. Checking
 * only what is already recorded gives every tech one free overshoot of
 * unlimited size: at exactly four hours on a four-hour job nothing is over
 * yet, so an eight-hour entry sails through and the gate fires for the first
 * time on a job already twelve hours deep.
 */
export function standing(variations: VariationSummary[], options: StandingOptions = {}): Standing {
  const { quoted_hours = null, hours_actual = 0, overrun_accepted_hours = null, proposed_hours = 0 } = options;

  const approved = variations.filter((v) => v.status === APPROVED);
  const pending = variations.filter((v) => (PENDING as readonly string[]).includes(v.status));

  const approvedCents = approved.reduce((sum, v) => sum + (v.amount_cents ?? 0), 0);
  const approvedHours = approved.reduce((sum, v) => sum + (v.hours ?? 0), 0);

  const ceiling =
    quoted_hours === null ? null : quoted_hours + approvedHours + (overrun_accepted_hours ?? 0);

  const warnings: string[] = [];
  let block: Block = "none";

  // Same order as `costing.Guard`: a pending variation outranks an overrun,
  // because it is the thing somebody can actually act on.
  if (pending.length > 0) {
    block = "variation_pending";
    const total = pending.reduce((sum, v) => sum + (v.amount_cents ?? 0), 0);
    warnings.push(
      `${pending.length} variation${pending.length > 1 ? "s" : ""} waiting on approval ` +
        `(${AUD.format(total / 100)}).`,
    );
  } else if (ceiling !== null && hours_actual + proposed_hours > ceiling) {
    block = "over_quoted_hours";
    const over = hours_actual + proposed_hours - ceiling;
    const lead = proposed_hours
      ? `This entry would take the job ${over.toFixed(1)} hours past`
      : `${over.toFixed(1)} hours past`;
    warnings.push(`${lead} the ${ceiling.toFixed(1)} it is approved for. Raise a variation, or have the office accept the overrun.`);
  }

  return {
    block,
    approved_cents: approvedCents,
    approved_hours: approvedHours,
    pending_count: pending.length,
    hours_ceiling: ceiling,
    warnings,
  };
}
